import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { techAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  MapPin, Phone, Navigation, Clock, ChevronRight,
  CheckCircle2, Loader2, Briefcase, Truck, AlertTriangle,
} from 'lucide-react';

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  en_route: 'En Route',
  on_site: 'On Site',
  done: 'Complete',
};
const STATUS_COLORS = {
  scheduled: 'bg-slate-100 text-slate-700',
  en_route: 'bg-blue-100 text-blue-800',
  on_site: 'bg-amber-100 text-amber-900',
  done: 'bg-emerald-100 text-emerald-800',
};
const WORKFLOW_ACTIONS = {
  scheduled: { label: 'On My Way',  icon: Truck,        next: 'en_route', color: 'bg-blue-600 active:bg-blue-700 text-white' },
  en_route:  { label: 'Arrived',    icon: MapPin,        next: 'on_site',  color: 'bg-amber-500 active:bg-amber-600 text-slate-950' },
  on_site:   { label: 'Finish Job', icon: CheckCircle2,  next: 'done',     color: 'bg-emerald-600 active:bg-emerald-700 text-white' },
};

function ActiveJobCard({ event, onTap, onStatusChange }) {
  const [updating, setUpdating] = useState(false);
  if (!event) return null;
  const cust = event.customer;
  const action = WORKFLOW_ACTIONS[event.status];
  const time = event.start_at
    ? new Date(event.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : '';
  const endTime = event.end_at
    ? new Date(event.end_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : '';

  const handleAction = async (e) => {
    e.stopPropagation();
    if (!action) return;
    setUpdating(true);
    try {
      await techAPI.updateStatus(event.event_id, action.next);
      onStatusChange(event.event_id, action.next);
      toast.success(STATUS_LABELS[action.next]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div
      className="w-full text-left border-2 border-blue-600 bg-white rounded-lg overflow-hidden shadow-sm"
      data-testid="active-job-card"
    >
      {/* Status strip */}
      <div className="bg-blue-600 px-4 py-1.5 flex items-center justify-between">
        <span className="text-[10px] font-bold text-white/80 uppercase tracking-widest">
          {event.status === 'on_site' ? 'Currently On Site' : event.status === 'en_route' ? 'Heading There' : 'Current Job'}
        </span>
        {event.priority === 'high' && (
          <Badge className="bg-red-500 text-white text-[10px] border-0">
            <AlertTriangle className="w-3 h-3 mr-0.5" /> Urgent
          </Badge>
        )}
      </div>

      <div className="p-4 cursor-pointer active:bg-slate-50" onClick={() => onTap(event.event_id)} role="button" tabIndex={0}>
        {/* Job name */}
        <h3 className="text-lg font-bold text-slate-950 leading-tight">
          {event.job_name || event.description || `Job #${event.job_number || ''}`}
        </h3>

        {/* Customer */}
        {cust && (
          <div className="mt-1.5">
            <p className="text-sm font-medium text-slate-600">{cust.name}</p>
            {cust.address && (
              <p className="text-xs text-slate-400 flex items-start gap-1 mt-0.5">
                <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                <span className="truncate">{cust.address}</span>
              </p>
            )}
          </div>
        )}

        {/* Time */}
        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
          <Clock className="w-3.5 h-3.5" />
          <span className="font-mono font-medium">{time}{endTime ? ` — ${endTime}` : ''}</span>
        </div>

        {/* Quick actions row */}
        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
          {cust?.address && (
            <a href={`https://maps.google.com/?q=${encodeURIComponent(cust.address)}`}
               target="_blank" rel="noreferrer"
               onClick={e => e.stopPropagation()}
               className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-blue-600 text-white text-xs font-bold active:bg-blue-700"
               data-testid="home-navigate">
              <Navigation className="w-3.5 h-3.5" strokeWidth={2.5} /> Navigate
            </a>
          )}
          {cust?.phone && (
            <a href={`tel:${cust.phone}`}
               onClick={e => e.stopPropagation()}
               className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold active:bg-slate-200"
               data-testid="home-call">
              <Phone className="w-3.5 h-3.5" strokeWidth={2.5} /> Call
            </a>
          )}
        </div>

        {/* Primary workflow action */}
        {action && (
          <button
            onClick={handleAction}
            disabled={updating}
            className={`w-full flex items-center justify-center gap-2 h-12 mt-3 rounded-sm text-sm font-bold ${action.color} ${updating ? 'opacity-60' : ''}`}
            data-testid="home-workflow-btn"
          >
            {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <><action.icon className="w-4 h-4" strokeWidth={2.5} /> {action.label}</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function NextJobCard({ event, onTap }) {
  if (!event) return null;
  const cust = event.customer;
  const time = event.start_at
    ? new Date(event.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : '';

  return (
    <button
      onClick={() => onTap(event.event_id)}
      className="w-full text-left border-2 border-slate-200 bg-white rounded-lg overflow-hidden active:scale-[0.99] transition-transform"
      data-testid="next-job-card"
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-mono text-slate-500">{time}</span>
          <ChevronRight className="w-4 h-4 text-slate-300" />
        </div>
        <h3 className="text-base font-bold text-slate-950 leading-tight">
          {event.job_name || event.description || `Job #${event.job_number || ''}`}
        </h3>
        {cust && (
          <div className="mt-1">
            <p className="text-sm text-slate-500">{cust.name}</p>
            {cust.address && (
              <p className="text-xs text-slate-400 flex items-start gap-1 mt-0.5">
                <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                <span className="truncate">{cust.address}</span>
              </p>
            )}
          </div>
        )}
        {/* Quick actions */}
        {cust && (
          <div className="flex gap-2 mt-2.5">
            {cust.address && (
              <a href={`https://maps.google.com/?q=${encodeURIComponent(cust.address)}`}
                 target="_blank" rel="noreferrer"
                 onClick={e => e.stopPropagation()}
                 className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-bold active:bg-blue-100">
                <Navigation className="w-3 h-3" /> Navigate
              </a>
            )}
            {cust.phone && (
              <a href={`tel:${cust.phone}`}
                 onClick={e => e.stopPropagation()}
                 className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-50 text-slate-600 text-xs font-bold active:bg-slate-100">
                <Phone className="w-3 h-3" /> Call
              </a>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

export default function TechHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await techAPI.myDay();
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const goToJob = (eventId) => navigate(`/tech/jobs/${eventId}`);

  const handleStatusChange = (eventId, newStatus) => {
    setData(prev => {
      if (!prev) return prev;
      const events = prev.events.map(e => e.event_id === eventId ? { ...e, status: newStatus } : e);
      const completed = events.filter(e => e.status === 'done');
      // Recalculate current and next
      let current_event = null;
      let next_event = null;
      for (const e of events) {
        if (['en_route', 'on_site'].includes(e.status)) { current_event = e; break; }
      }
      if (current_event) {
        for (const e of events) {
          if (e.event_id !== current_event.event_id && e.status === 'scheduled') { next_event = e; break; }
        }
      } else {
        for (const e of events) {
          if (e.status === 'scheduled') { next_event = e; break; }
        }
      }
      return { ...prev, events, completed_events: completed.length, current_event, next_event };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const remaining = data?.events?.filter(e => e.status !== 'done') || [];
  const done = data?.events?.filter(e => e.status === 'done') || [];
  const firstName = user?.name?.split(' ')[0] || 'Tech';

  return (
    <div className="px-4 pt-5 pb-4 max-w-lg mx-auto" data-testid="tech-home">
      {/* Greeting row */}
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </p>
          <h1 className="text-2xl font-bold text-slate-950 mt-0.5" data-testid="tech-greeting">
            Hey, {firstName}
          </h1>
        </div>
        {/* Summary counts */}
        <div className="flex items-center gap-3 text-right">
          <div>
            <p className="text-xl font-bold font-mono text-slate-950">{remaining.length}</p>
            <p className="text-[9px] uppercase tracking-wide text-slate-400 font-bold">Left</p>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div>
            <p className="text-xl font-bold font-mono text-emerald-600">{done.length}</p>
            <p className="text-[9px] uppercase tracking-wide text-slate-400 font-bold">Done</p>
          </div>
        </div>
      </div>

      {/* Current Active Job — dominant hero card */}
      {data?.current_event && (
        <div className="mb-4">
          <ActiveJobCard
            event={data.current_event}
            onTap={goToJob}
            onStatusChange={handleStatusChange}
          />
        </div>
      )}

      {/* Next Job */}
      {data?.next_event && (
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 px-0.5">Next Up</p>
          <NextJobCard event={data.next_event} onTap={goToJob} />
        </div>
      )}

      {/* Remaining (when no current/next — e.g. all scheduled, first one not started) */}
      {remaining.length > 0 && !data?.current_event && !data?.next_event && (
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Today's Jobs</p>
          <div className="space-y-2">
            {remaining.map(evt => (
              <NextJobCard key={evt.event_id} event={evt} onTap={goToJob} />
            ))}
          </div>
        </div>
      )}

      {/* Extra remaining jobs beyond current+next */}
      {data?.current_event && remaining.filter(e => 
        e.event_id !== data.current_event?.event_id && e.event_id !== data.next_event?.event_id
      ).length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 px-0.5">Later Today</p>
          <div className="space-y-2">
            {remaining.filter(e => 
              e.event_id !== data.current_event?.event_id && e.event_id !== data.next_event?.event_id
            ).map(evt => (
              <button
                key={evt.event_id}
                onClick={() => goToJob(evt.event_id)}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 bg-white border border-slate-200 rounded-lg active:bg-slate-50"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-700 truncate">{evt.job_name || `Job #${evt.job_number}`}</p>
                  <p className="text-xs text-slate-400">
                    {evt.customer?.name} · {evt.start_at ? new Date(evt.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {(!data || data.total_events === 0) && (
        <div className="text-center py-16">
          <Briefcase className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-lg font-bold text-slate-400">No jobs today</p>
          <p className="text-sm text-slate-300 mt-1">Check your schedule for upcoming work</p>
          <button
            onClick={() => navigate('/tech/schedule')}
            className="mt-4 px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-sm active:bg-blue-700"
            data-testid="view-schedule-btn"
          >
            View Schedule
          </button>
        </div>
      )}

      {/* Completed jobs */}
      {done.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 mb-2 flex items-center gap-1 px-0.5">
            <CheckCircle2 className="w-3 h-3" /> Completed
          </p>
          <div className="space-y-1.5">
            {done.map(evt => (
              <button
                key={evt.event_id}
                onClick={() => goToJob(evt.event_id)}
                className="w-full text-left flex items-center gap-3 px-3 py-2 bg-white border border-slate-100 rounded-lg active:bg-slate-50"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-600 truncate">{evt.job_name || `Job #${evt.job_number}`}</p>
                  <p className="text-xs text-slate-400">{evt.customer?.name}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
